import { Badge } from '@repo/ui/components/ui/badge'
import { Button } from '@repo/ui/components/ui/button'
import { Loader2 } from 'lucide-react'
import { useEffect, useState } from 'react'
import { toast } from 'sonner'
import { AuthProviderIcon, formatAuthProviderName } from '@/components/auth/shared'
import { authClient, authSocialProviders } from '@/lib/auth-client'

export function AccountsTab() {
  const [linkedProviders, setLinkedProviders] = useState<string[]>([])
  const [loadingProvider, setLoadingProvider] = useState<string | null>(null)
  const [isLoadingAccounts, setIsLoadingAccounts] = useState(true)

  const providers = authSocialProviders.map((id) => ({
    id,
    name: formatAuthProviderName(id),
  }))

  const refreshAccounts = async () => {
    try {
      const accounts = await authClient.listAccounts()
      if (accounts.data) {
        const providerIds = accounts.data
          .map((account) => account.providerId)
          .filter((id): id is string => id !== null && id !== undefined)
        setLinkedProviders(providerIds)
      }
    } catch {
      toast.error('Failed to load linked accounts')
    } finally {
      setIsLoadingAccounts(false)
    }
  }

  // biome-ignore lint/correctness/useExhaustiveDependencies: only run on mount
  useEffect(() => {
    void refreshAccounts()
  }, [])

  const handleLinkProvider = async (providerId: string) => {
    setLoadingProvider(providerId)
    try {
      await authClient.linkSocial({ provider: providerId })
      toast.success(`${formatAuthProviderName(providerId)} account linked`)
      await refreshAccounts()
    } catch {
      toast.error(`Failed to link ${formatAuthProviderName(providerId)}`)
    } finally {
      setLoadingProvider(null)
    }
  }

  const handleUnlinkProvider = async (providerId: string) => {
    if (linkedProviders.length <= 1) {
      toast.error('Cannot unlink account', {
        description: 'You must have at least one connected account',
      })
      return
    }

    setLoadingProvider(providerId)
    try {
      await authClient.unlinkAccount({ providerId })
      toast.success(`${formatAuthProviderName(providerId)} account unlinked`)
      await refreshAccounts()
    } catch {
      toast.error(`Failed to unlink ${formatAuthProviderName(providerId)}`)
    } finally {
      setLoadingProvider(null)
    }
  }

  return (
    <div className="flex flex-col gap-3">
      <div>
        <h3 className="text-base font-semibold tracking-tight">Sign-in providers</h3>
        <p className="mt-1 text-sm text-muted-foreground">
          These accounts are for authentication only and do not grant calendar access.
        </p>
      </div>
      <div className="flex flex-col gap-2">
        {providers.map((provider) => {
          const isLinked = linkedProviders.includes(provider.id)
          const isLoading = loadingProvider === provider.id

          return (
            <div
              key={provider.id}
              className="flex items-center justify-between rounded-[4px] border border-border/60 bg-card px-5 py-3"
            >
              <div className="flex items-center gap-3">
                <span className="inline-flex size-7 items-center justify-center rounded-full bg-muted text-foreground">
                  <AuthProviderIcon provider={provider.id} className="size-4" />
                </span>
                <span className="font-medium">{provider.name}</span>
                {isLinked && <Badge variant="secondary">Connected</Badge>}
              </div>
              <Button
                variant="ghost"
                size="sm"
                className="cursor-pointer"
                onClick={() =>
                  isLinked ? handleUnlinkProvider(provider.id) : handleLinkProvider(provider.id)
                }
                disabled={isLoading || isLoadingAccounts}
              >
                {isLoading ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : isLinked ? (
                  'Unlink'
                ) : (
                  'Link'
                )}
              </Button>
            </div>
          )
        })}
      </div>
    </div>
  )
}
